import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Send, CheckCircle, MessageCircle, PenTool } from 'lucide-react';
import { toast } from 'sonner';
import { businessInfo } from '../shopData';
import useDocumentTitle from '../hooks/useDocumentTitle';


const WhatsAppIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const CustomisedOrders = () => {
  const navigate = useNavigate();

  useDocumentTitle(
    'Customised Silver Orders | Shri Kothari Jewellers',
    "Commission a one-of-a-kind silver piece. Wedding silver, heirloom recreations, gift sets and more — crafted in pure silver by Shri Kothari Jewellers, Indore."
  );

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const buildCustomWhatsAppMessage = () => {
    const lines = [
      "Hi Shri Kothari Jewellers,",
      "",
      "I'd like to place a customised silver order.",
      "",
      `Name: ${formData.name}`,
      `Phone: ${formData.phone}`,
      `Email: ${formData.email}`,
      "",
      "What I'd like made:",
      formData.message,
    ];
    return lines.join("\n");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const waCustomUrl = `https://wa.me/${businessInfo.whatsappNumber}?text=${encodeURIComponent(buildCustomWhatsAppMessage())}`;
    const waTab = window.open('about:blank', '_blank');

    toast.success('Opening WhatsApp...');
    setIsSubmitted(true);

    if (waTab && !waTab.closed) {
      waTab.location = waCustomUrl;
    } else {
      window.location.href = waCustomUrl;
    }

    setIsSubmitting(false);

    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', message: '' });
      setIsSubmitted(false);
    }, 5000);
  };

  const waLink = `https://wa.me/${businessInfo.whatsappNumber}?text=${encodeURIComponent(
    "Hi, I'd like to order something custom-made in silver. Can you help me?"
  )}`;

  const scrollToForm = () => {
    document.getElementById('custom-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="customised-page" data-testid="customised-orders-page">
      <div className="customised-page-inner">
        <button
          onClick={() => navigate(-1)}
          className="customised-page-back"
          data-testid="customised-back-btn"
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        {/* Hero */}
        <div className="customised-hero">
          <div className="customised-hero-eyebrow">Custom Made · Since 1987</div>
          <h1 className="customised-hero-title">
            Make It Your Own
          </h1>
          <p className="customised-hero-desc">
            Want something unique? Tell us what you have in mind &mdash; a special gift, wedding silver,
            or a family keepsake &mdash; and our craftsmen will make it in pure silver, just for you.
          </p>
        </div>

        {/* 3 Options */}
        <div className="customised-options-grid">
          <a
            href={waLink}
            target="_blank"
            rel="noopener noreferrer"
            className="customised-option"
            data-testid="custom-option-whatsapp"
          >
            <div className="customised-option-icon">
              <WhatsAppIcon size={26} />
            </div>
            <div className="customised-option-title">Chat on WhatsApp</div>
            <div className="customised-option-desc">
              Quickest way. Send us a photo or description &mdash; we'll reply with a price.
            </div>
          </a>

          <a
            href={`tel:${businessInfo.phone}`}
            className="customised-option"
            data-testid="custom-option-call"
          >
            <div className="customised-option-icon">
              <Phone size={26} />
            </div>
            <div className="customised-option-title">Call Us</div>
            <div className="customised-option-desc">
              Talk to us directly &mdash; {businessInfo.phone}
              <br />
              <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>Mon &ndash; Sat, 11:30 AM &ndash; 8:30 PM</span>
            </div>
          </a>

          <button
            type="button"
            onClick={scrollToForm}
            className="customised-option"
            data-testid="custom-option-form"
          >
            <div className="customised-option-icon">
              <PenTool size={26} />
            </div>
            <div className="customised-option-title">Fill the Form</div>
            <div className="customised-option-desc">
              Tell us what you need below &mdash; we'll get back within a day.
            </div>
          </button>
        </div>

        {/* Form */}
        <div className="customised-form-section" id="custom-form">
          {!isSubmitted ? (
            <>
              <div className="customised-form-heading">
                <h2>Tell Us What You Want</h2>
                <p>Fill in a few details and we'll reach out within 24 hours.</p>
              </div>
              <form onSubmit={handleSubmit} className="customised-form">
                <div className="customised-form-row">
                  <div className="customised-form-field">
                    <label>Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Enter your name"
                      data-testid="custom-form-name"
                    />
                  </div>
                  <div className="customised-form-field">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+91 XXXXX XXXXX"
                      data-testid="custom-form-phone"
                    />
                  </div>
                </div>
                <div className="customised-form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    data-testid="custom-form-email"
                  />
                </div>
                <div className="customised-form-field">
                  <label>What would you like us to make?</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="For example: A silver bangle like my grandmother's, a thali set for a wedding, or a 6-inch Ganesh idol..."
                    data-testid="custom-form-message"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="customised-form-submit"
                  data-testid="custom-form-submit"
                >
                  <Send size={18} />
                  <span>{isSubmitting ? 'Sending...' : 'Send'}</span>
                </button>
              </form>
            </>
          ) : (
            <div className="customised-success" data-testid="custom-form-success">
              <CheckCircle size={64} style={{ color: 'var(--gold-primary)', margin: '0 auto' }} />
              <h3>Thank You!</h3>
              <p>We've received your request. WhatsApp should have opened in a new tab so you can confirm by sending the prefilled message.</p>
              <a
                href={`https://wa.me/${businessInfo.whatsappNumber}?text=${encodeURIComponent(
                  [
                    "Hi Shri Kothari Jewellers,",
                    "",
                    "I'd like to place a customised silver order.",
                    "",
                    `Name: ${formData.name}`,
                    `Phone: ${formData.phone}`,
                    `Email: ${formData.email}`,
                    "",
                    "What I'd like made:",
                    formData.message,
                  ].join("\n")
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="lux-btn-outline"
                data-testid="custom-form-success-wa"
                style={{ marginTop: '1.25rem', display: 'inline-flex' }}
              >
                <WhatsAppIcon size={18} />
                <span>Open WhatsApp Manually</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomisedOrders;
